"use client";

import { useCallback, useEffect, useState } from "react";
import {
  FILLED_FILE_POLL_INTERVAL_MS,
  FILLED_FILE_POLL_MAX_ATTEMPTS,
} from "@/features/forms/_logic/constants/file-constants";

/** Filled-document generation status shown in the thank-you state. */
export type FilledFileStatus = "idle" | "pending" | "ready" | "failed";

interface FilledFilePollTarget {
  responseId: number;
  token: string;
  expiry: number;
}

interface UseFilledFilePollReturn {
  status: FilledFileStatus;
  pdfUrl: string | null;
  docxUrl: string | null;
  /** Starts polling `/api/forms/filled-pdf/:responseId` for the given signed download token. */
  startPolling: (target: FilledFilePollTarget) => void;
}

/**
 * Polls the public filled-PDF/DOCX endpoint after a form submission that
 * autofills a file template, until the file is ready, generation fails, or
 * the attempt ceiling is hit. Polling is cleaned up on unmount or when a new
 * poll target replaces the current one.
 *
 * @returns The current generation status, download URLs (once ready), and the starter function.
 */
export function useFilledFilePoll(): UseFilledFilePollReturn {
  const [status, setStatus] = useState<FilledFileStatus>("idle");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [docxUrl, setDocxUrl] = useState<string | null>(null);
  const [target, setTarget] = useState<FilledFilePollTarget | null>(null);

  const startPolling = useCallback((next: FilledFilePollTarget) => {
    setStatus("pending");
    setPdfUrl(null);
    setDocxUrl(null);
    setTarget(next);
  }, []);

  useEffect(() => {
    if (!target) return;

    let cancelled = false;
    let attempts = 0;
    const { responseId, token, expiry } = target;

    const tick = async () => {
      attempts += 1;
      try {
        const url = `/api/forms/filled-pdf/${responseId}?token=${encodeURIComponent(token)}` + `&expiry=${expiry}`;
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) {
          if (!cancelled) setStatus("failed");
          return;
        }
        const data = (await res.json()) as
          | { status: "pending" }
          | { status: "failed" }
          | { status: "ready"; downloadUrl?: string; docxDownloadUrl?: string };

        if (cancelled) return;

        if (data.status === "ready") {
          setPdfUrl(data.downloadUrl ?? null);
          setDocxUrl(data.docxDownloadUrl ?? null);
          setStatus("ready");
          return;
        }
        if (data.status === "failed") {
          setStatus("failed");
          return;
        }
        if (attempts >= FILLED_FILE_POLL_MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        timer = setTimeout(tick, FILLED_FILE_POLL_INTERVAL_MS);
      } catch (pollError) {
        console.error("useFilledFilePoll: poll failed", pollError);
        if (!cancelled) setStatus("failed");
      }
    };

    let timer = setTimeout(tick, FILLED_FILE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [target]);

  return { status, pdfUrl, docxUrl, startPolling };
}
